/**
 * Helper functions for identifying and working with interactive 3D elements
 */

import * as THREE from 'three';
import { logger } from './logger';

// Store reference to canvas element ref from context
let canvasElementRefFromContext = null;

/**
 * Sets the canvas element ref from context
 * @param {React.RefObject} ref - The canvas element ref from CanvasContext
 */
export const setCanvasElementRef = (ref) => {
  canvasElementRefFromContext = ref;
};

/**
 * Gets the canvas element (shared across all 3D scenes)
 * @returns {HTMLCanvasElement|null} - The canvas element or null if not found
 */
export const getCanvasElement = () => {
  // Use the ref from context if available
  if (canvasElementRefFromContext?.current) {
    return canvasElementRefFromContext.current;
  }
  // Fallback to DOM query if ref not available yet
  return document.querySelector('canvas') || null;
};

/**
 * Sets the cursor globally on the body element and canvas element
 * @param {boolean} isPointer - If true, sets cursor to pointer; if false, sets to default
 */
export const setCanvasPointer = (isPointer = true) => {
  const body = document.body;
  if (body) {
    if (isPointer) {
      body.classList.add('cursor-pointer');
      body.classList.remove('cursor-default');
    } else {
      body.classList.remove('cursor-pointer');
      body.classList.add('cursor-default');
    }
  } else {
    logger.warn('Body element not found in setCanvasPointer');
  }
  
  // Also set cursor directly on canvas element to ensure it applies
  const canvas = getCanvasElement();
  if (canvas) {
    if (isPointer) {
      canvas.style.cursor = 'url("/icons/pointer.svg") 10 0, pointer';
    } else {
      canvas.style.cursor = 'auto';
    }
  }
};

/**
 * Checks if an object (or any parent in its hierarchy) matches a given name pattern
 * @param {Object} obj - The Three.js object to check
 * @param {string|Array<string>} names - Name(s) to match (case-insensitive)
 * @param {number} maxDepth - Maximum depth to traverse up the hierarchy
 * @returns {boolean} - True if the object or any parent matches
 */
export const isObjectNamed = (obj, names, maxDepth = 5) => {
  if (!obj) return false;
  
  const nameArray = Array.isArray(names) ? names : [names];
  const normalizedNames = nameArray.map(n => n.toLowerCase().trim());
  
  const checkObject = (currentObj, depth = 0) => {
    if (depth > maxDepth) return false;
    if (!currentObj) return false;
    
    const objName = (currentObj.name || '').toLowerCase().trim();
    const origName = (currentObj.userData?.originalName || '').toLowerCase().trim();
    
    // Check if current object matches (exact match or contains)
    for (const normalizedName of normalizedNames) {
      // Exact match
      if (objName === normalizedName || origName === normalizedName) {
        return true;
      }
      // Contains match (for partial names)
      if (normalizedName.length > 0 && 
          (objName.includes(normalizedName) || origName.includes(normalizedName))) {
        return true;
      }
    }
    
    // Check parent
    if (currentObj.parent) {
      return checkObject(currentObj.parent, depth + 1);
    }
    
    return false;
  };
  
  return checkObject(obj);
};

/**
 * Gets all names in an object's hierarchy (for debugging)
 * @param {Object} obj - The Three.js object
 * @param {number} maxDepth - Maximum depth to traverse
 * @returns {Array<{name: string, origName: string}>} - Array of name objects
 */
export const getObjectHierarchyNames = (obj, maxDepth = 5) => {
  const names = [];
  let current = obj;
  let depth = 0;
  
  while (current && depth <= maxDepth) {
    names.push({
      name: current.name || '',
      origName: current.userData?.originalName || ''
    });
    current = current.parent;
    depth++;
  }
  
  return names;
};

/**
 * Extracts name information from an object (for consistency with click handlers)
 * @param {Object} obj - The Three.js object
 * @returns {{name: string, origName: string}} - Object with name and originalName
 */
export const getObjectNameInfo = (obj) => {
  const name = obj?.name || '';
  const origName = obj?.userData?.originalName || '';
  return { name, origName };
};

/**
 * Gets all objects in the hierarchy starting from a given object
 * Similar to how click handlers work - traverses up the parent chain
 * @param {Object} obj - The Three.js object to start from
 * @param {number} maxDepth - Maximum depth to traverse (default: 5)
 * @returns {Array<{name: string, origName: string}>} - Array of name info objects
 */
export const getObjectHierarchy = (obj, maxDepth = 5) => {
  const namesToCheck = [];
  let currentObject = obj;
  let depth = 0;
  
  while (currentObject && depth < maxDepth) {
    namesToCheck.push(getObjectNameInfo(currentObject));
    currentObject = currentObject.parent;
    depth++;
  }
  
  return namesToCheck;
};

/**
 * Checks all objects under the mouse cursor using raycasting
 * Similar to how click handlers work - finds ALL objects at the cursor position
 * @param {Object} event - React Three Fiber pointer event (from onPointerMove)
 * @param {Object} sceneObject - The scene object to raycast against
 * @param {Function} isInteractiveCheck - Function to check if an object is interactive (e.g., isTvInteractiveElement)
 * @param {boolean} debug - Whether to log debug information
 * @returns {boolean} - True if any object under the cursor is interactive
 */
export const checkAllObjectsUnderCursor = (event, sceneObject, isInteractiveCheck, debug = false) => {
  if (!event || !sceneObject || !isInteractiveCheck) return false;
  
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  
  // Get the canvas element
  const canvas = getCanvasElement();
  if (!canvas) {
    if (debug) logger.warn('Canvas element not found');
    return false;
  }
  
  // Get normalized device coordinates (-1 to +1) for both components
  // React Three Fiber events have nativeEvent with clientX/clientY
  const nativeEvent = event.nativeEvent || event;
  const rect = canvas.getBoundingClientRect();
  
  // Use clientX/clientY from the native event
  const clientX = nativeEvent.clientX ?? nativeEvent.touches?.[0]?.clientX;
  const clientY = nativeEvent.clientY ?? nativeEvent.touches?.[0]?.clientY;
  
  if (clientX === undefined || clientY === undefined) {
    if (debug) logger.warn('Could not get clientX/clientY from event', event);
    return false;
  }
  
  mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
  mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;
  
  // Use the camera from the event
  const camera = event.camera;
  if (!camera) {
    if (debug) logger.warn('No camera found in event');
    return false;
  }
  
  // Update the raycaster with the mouse position and camera
  raycaster.setFromCamera(mouse, camera);
  
  // Get all intersections (not just the first one)
  const intersects = raycaster.intersectObject(sceneObject, true);  
  const checkedObjects = [];
  
  // Check all intersected objects and their hierarchies
  // Use the same helper function as click handlers for consistency
  for (const intersection of intersects) {
    // Use getObjectHierarchy to get all objects in the hierarchy (same as onClick handler)
    const hierarchy = getObjectHierarchy(intersection.object, 10);
    
    // Check each object in the hierarchy
    let currentObject = intersection.object;
    for (let i = 0; i < hierarchy.length; i++) {
      const { name, origName } = hierarchy[i];
      
      const objInfo = {
        depth: i,
        distance: intersection.distance,
        name,
        origName,
        type: currentObject?.type || '',
        isInteractive: isInteractiveCheck(currentObject)
      };
      
      // Only add if we haven't checked this object already
      const alreadyChecked = checkedObjects.some(
        o => o.name === objInfo.name && o.origName === objInfo.origName && o.depth === objInfo.depth
      );
      
      if (!alreadyChecked) {
        checkedObjects.push(objInfo);
      }
      
      if (objInfo.isInteractive) {
        return true;
      }
      
      // Move to parent for next iteration
      currentObject = currentObject?.parent;
    }
  }
  
  return false;
};

/**
 * Checks if normalized names match any of the given patterns
 * Used by both click handlers and isTvInteractiveElement for consistency
 * @param {string} normalizedName - The normalized name to check
 * @param {string} normalizedOrig - The normalized original name to check
 * @param {Array<string>} patterns - Array of patterns to match against
 * @returns {boolean} - True if any pattern matches
 */
export const matchesNamePatterns = (normalizedName, normalizedOrig, patterns) => {
  return patterns.some(pattern => 
    normalizedName === pattern || normalizedOrig === pattern ||
    normalizedName.includes(pattern) || normalizedOrig.includes(pattern)
  );
};

/**
 * Checks if an object matches TV button 1 patterns
 * @param {string} normalizedName - The normalized name to check
 * @param {string} normalizedOrig - The normalized original name to check
 * @returns {boolean} - True if it matches button 1 patterns
 */
export const isTvButton1 = (normalizedName, normalizedOrig) => {
  const button1Names = ['buton_1', 'button_1', 'buton1', 'button1', 'btn1', 'btn_1'];
  return matchesNamePatterns(normalizedName, normalizedOrig, button1Names);
};

/**
 * Checks if an object matches TV button 2 patterns
 * @param {string} normalizedName - The normalized name to check
 * @param {string} normalizedOrig - The normalized original name to check
 * @returns {boolean} - True if it matches button 2 patterns
 */
export const isTvButton2 = (normalizedName, normalizedOrig) => {
  const button2Names = ['button_2_main', 'button_2', 'button2_main', 'button2', 'buton_2', 'btn2', 'btn_2'];
  return matchesNamePatterns(normalizedName, normalizedOrig, button2Names);
};

/**
 * Checks if an object matches TV screen display patterns
 * @param {string} normalizedName - The normalized name to check
 * @param {string} normalizedOrig - The normalized original name to check
 * @returns {boolean} - True if it matches screen display patterns
 */
export const isTvScreenDisplay = (normalizedName, normalizedOrig) => {
  const screenNames = ['screen-display', 'screendisplay', 'screen_display'];
  return matchesNamePatterns(normalizedName, normalizedOrig, screenNames);
};

/**
 * Checks if an object is an interactive TV element (button_1, button_2, screen-display)
 * Uses the same helper functions as click handlers for consistency
 * @param {Object} obj - The Three.js object to check
 * @returns {boolean} - True if it's an interactive TV element
 */
export const isTvInteractiveElement = (obj) => {
  if (!obj) return false;
  
  // First check if it's marked as a TV interactive element in userData
  if (obj.userData?.isTvInteractive === true) {
    return true;
  }
  
  // Use getObjectNameInfo for consistency with click handlers
  const { name, origName } = getObjectNameInfo(obj);
  const normalizedName = name.toLowerCase();
  const normalizedOrig = origName.toLowerCase();
  
  // Check against all TV interactive patterns
  return isTvButton1(normalizedName, normalizedOrig) ||
         isTvButton2(normalizedName, normalizedOrig) ||
         isTvScreenDisplay(normalizedName, normalizedOrig);
};

/**
 * Checks if an object is an interactive News element (newsbox-blue)
 * @param {Object} obj - The Three.js object to check
 * @returns {boolean} - True if it's an interactive News element (only newsbox-blue)
 */
export const isNewsInteractiveElement = (obj) => {
  if (!obj) return false;
  
  // Only newsbox-blue should be interactive - check name patterns
  const newsboxNames = ['newsbox-blue', 'newsboxblue', 'newsbox_blue'];
  if (isObjectNamed(obj, newsboxNames)) {
    return true;
  }
  
  // Check if explicitly marked as news interactive (not just clickable)
  if (obj.userData?.isNewsInteractive === true) {
    return true;
  }
  
  return false;
};

/**
 * Checks if an object is an interactive Phonebox element
 * @param {Object} obj - The Three.js object to check
 * @returns {boolean} - True if it's an interactive Phonebox element
 */
export const isPhoneboxInteractiveElement = (obj) => {
  if (!obj) return false;
  
  // Check if marked as clickable
  if (obj.userData?.isClickable) {
    return true;
  }
  
  // Phonebox is generally clickable
  return true;
};

/**
 * Checks if an object is any interactive element
 * @param {Object} obj - The Three.js object to check
 * @param {string} sceneType - Type of scene: 'tv', 'news', 'phonebox', or 'background'
 * @returns {boolean} - True if it's an interactive element
 */
export const isInteractiveElement = (obj, sceneType = null) => {
  if (!obj) return false;
  
  // Check based on scene type
  if (sceneType === 'tv') {
    return isTvInteractiveElement(obj);
  }
  
  if (sceneType === 'news') {
    return isNewsInteractiveElement(obj);
  }
  
  if (sceneType === 'phonebox' || sceneType === 'background') {
    return isPhoneboxInteractiveElement(obj);
  }
  
  // Generic check: if marked as clickable
  if (obj.userData?.isClickable) {
    return true;
  }
  
  // Fallback: check all known interactive elements
  return isTvInteractiveElement(obj) || 
         isNewsInteractiveElement(obj) || 
         isPhoneboxInteractiveElement(obj);
};

